import { useMutation, useQuery } from '@apollo/client'
import { Card, CardContent, CardHeader, CardTitle, MapPin, Skeleton } from '@chain-zen/ui'
import dynamic from 'next/dynamic'
import { MapProvider } from 'react-map-gl/mapbox'
import { toast } from 'sonner'

import { TrackingSources } from '../../../../../components/tracking-sources'
import { AddTiveDevice } from '../../../../../components/tracking-sources/add-tive-device'
import { AddTrackingLink } from '../../../../../components/tracking-sources/add-tracking-link'
import { UPDATE_STOP_COORDINATES } from '../../../../../data/mutations/tracking'
import { GET_APP_API_KEY } from '../../../../../data/queries/settings'
import { SHIPMENT_TRACKING_SOURCES } from '../../../../../data/queries/tracking'
import type { TAppAPIKeyReturnType } from '../../../../../types/settings'
import type { IStop, TShipmentMember, TShipmentTrackingReturnType } from '../../../../../types/shipment'
import { AppTypeEnum } from '../../../../../types/workspace'
import { TrackingWindow } from './tracking-window'

const Map = dynamic(() => import('../../../../../components/map').then((mod) => ({ default: mod.Map })), {
  ssr: false,
  loading: () => <div className="w-full h-64 bg-gray-200 rounded-lg animate-pulse" />
})

interface TrackingProps {
  drivers?: TShipmentMember[]
  isUserAdminOrOwner?: boolean
  shipmentID: string
  shipmentNumber: string
  shipmentOwnerEnterpriseWorkspaceID?: string
  shipmentOwnerWorkspaceID?: string
  stops: IStop[]
}

const LoadingFallback = () => (
  <div className="w-full h-full flex items-center justify-center">
    <Skeleton className="w-full h-64 rounded-lg" />
  </div>
)

/*
 * This component is used to display the tracking information of a shipment.
 * It displays the tracking sources, device management, external tracking link, and the map of the shipment.
 */
export const Tracking = ({
  shipmentID,
  shipmentNumber,
  stops,
  isUserAdminOrOwner,
  drivers,
  shipmentOwnerEnterpriseWorkspaceID,
  shipmentOwnerWorkspaceID
}: TrackingProps) => {
  const { data, loading: loadingTrackingSources } = useQuery<
    {
      trackingSources: TShipmentTrackingReturnType
    },
    { shipmentID: string }
  >(SHIPMENT_TRACKING_SOURCES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    pollInterval: 30000,
    variables: { shipmentID }
  })

  const { data: tiveAppData } = useQuery<TAppAPIKeyReturnType>(GET_APP_API_KEY, {
    fetchPolicy: 'network-only',
    variables: { appType: AppTypeEnum.Tive }
  })

  const isTiveAppEnabled = tiveAppData?.appAPIKey?.enabled

  const [updateStopCoordinates, { loading }] = useMutation(UPDATE_STOP_COORDINATES, {
    onCompleted: () => {
      toast.success('Stop coordinates updated successfully')
    },
    onError: () => {
      toast.error('Failed to update stop coordinates')
    },
    refetchQueries: [SHIPMENT_TRACKING_SOURCES]
  })

  const handleCoordinatesChange = (id: string, coords: { latitude: number; longitude: number }) => {
    const availableStop = stops.find((stop) => stop.id === id)

    if (!availableStop) return
    const address = availableStop.warehouse?.address
    const input = {
      address: {
        addressLabel: address?.addressLabel,
        boundingBox: address?.boundingBox,
        city: address?.city,
        coordinates: coords,
        country: address?.country,
        countryCode: address?.countryCode,
        county: address?.county,
        postalCode: address?.postalCode,
        state: address?.state
      },
      locationID: availableStop.warehouse?.locationID,
      shipmentID,
      shipmentOwnerEnterpriseWorkspaceID,
      shipmentOwnerWorkspaceID,
      stopID: id
    }
    updateStopCoordinates({ variables: input })
  }

  if (loadingTrackingSources && !data?.trackingSources) {
    return <LoadingFallback />
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          Tracking
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <TrackingWindow shipmentID={shipmentID} />
          {/* Tracking Sources Section */}
          {!!data?.trackingSources && (
            <TrackingSources
              shipmentID={shipmentID}
              shipmentNumber={shipmentNumber}
              shipmentOwnerWorkspaceID={shipmentOwnerWorkspaceID}
              shipmentTracking={data.trackingSources}
              drivers={drivers}
            />
          )}

          {/* Device Management Section */}

          {!!isTiveAppEnabled && <AddTiveDevice shipmentID={shipmentID} />}

          {/* External Tracking Link Section */}
          <AddTrackingLink shipmentID={shipmentID} />

          {/* Map Section */}
          <div className="h-[31rem] w-full overflow-hidden rounded-lg">
            <MapProvider>
              <Map
                enableGeofenceMode={true}
                shipmentOwnerEnterpriseWorkspaceID={shipmentOwnerEnterpriseWorkspaceID}
                shipmentOwnerWorkspaceID={shipmentOwnerWorkspaceID}
                shipmentTracking={data?.trackingSources || null}
                shipmentID={shipmentID}
                stops={stops}
                isStopCoordinatesLoading={loading}
                onCoordinatesChange={handleCoordinatesChange}
                isUserAdminOrOwner={isUserAdminOrOwner}
              />
            </MapProvider>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
